import { inject, Injectable, signal } from '@angular/core';
import { ProductDetailsRepository } from '../repositories/product-details.repository';
import { StockPostingProduct } from '../types/stock-posting-product';

/**
 * Keyed cache of product details. Rows, the item form and the search predicate
 * share one request per product instead of fetching per component instance.
 */
@Injectable()
export class ProductDetailsService {
  private readonly repository = inject(ProductDetailsRepository);
  private readonly pending = new Map<number, Promise<StockPostingProduct | null>>();
  private readonly cache = signal<ReadonlyMap<number, StockPostingProduct>>(new Map());

  /** Reactive read; `null` until `load()` (or `prime()`) has resolved the product. */
  get(productId: number): StockPostingProduct | null {
    return this.cache().get(productId) ?? null;
  }

  load(productId: number): Promise<StockPostingProduct | null> {
    const cached = this.get(productId);
    if (cached) return Promise.resolve(cached);
    const pending = this.pending.get(productId);
    if (pending) return pending;

    const request = this.repository
      .load(productId)
      .then((product) => {
        this.prime(product);
        return product;
      })
      .catch(() => null)
      .finally(() => this.pending.delete(productId));
    this.pending.set(productId, request);
    return request;
  }

  /** Seeds the cache with products that arrived embedded in another response. */
  prime(...products: StockPostingProduct[]): void {
    if (products.length === 0) return;
    this.cache.update((current) => {
      const next = new Map(current);
      for (const product of products) next.set(product.id, product);
      return next;
    });
  }
}
